import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiInput from "components/VuiInput";
import { useAuthz } from "context/AuthzContext";
import assistantApi from "../services/assistantApi";

export default function AssistantPane() {
  const { tenantId, isSuperAdmin } = useAuthz();
  const history = useHistory();
  const { pathname } = useLocation();
  const [task, setTask] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [licenseBlocked, setLicenseBlocked] = useState(false);

  const inferClientIdFromPath = () => {
    const m = pathname.match(/^\/client-profiles\/([^/]+)/);
    return m ? m[1] : null;
  };

  const handleAsk = async () => {
    setError(null);
    setLicenseBlocked(false);
    setLoading(true);
    try {
      const clientId = inferClientIdFromPath();
      const res = await assistantApi.queryAssistant(tenantId, { task, contextFilters: clientId ? { clientId } : undefined });
      setResult(res);
    } catch (err) {
      // Detect license-related 403 from API
      const status = err?.status || err?.payload?.status;
      const apiMessage = err?.payload?.error || err?.message || "Request failed";
      if (status === 403 && /license|not enabled|not enabled for tenant|not enabled for/i.test(apiMessage)) {
        setLicenseBlocked(true);
        setError(apiMessage);
      } else {
        setError(apiMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async (suggestion) => {
    setError(null);
    setLicenseBlocked(false);
    setLoading(true);
    try {
      const res = await assistantApi.executeAssistant(tenantId, { requestId: result.requestId, action: suggestion, executeMode: "dry" });
      // show response
      setResult((r) => ({ ...r, execResult: res }));
    } catch (err) {
      const status = err?.status || err?.payload?.status;
      const apiMessage = err?.payload?.error || err?.message || "Execute failed";
      if (status === 403 && /license|not enabled|not enabled for tenant|not enabled for/i.test(apiMessage)) {
        setLicenseBlocked(true);
        setError(apiMessage);
      } else {
        setError(apiMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <VuiBox sx={{ mt: 2, p: 2, borderRadius: 2, background: "rgba(255,255,255,0.02)" }}>
      <VuiTypography variant="lg" fontWeight="bold">Assistant (PoC)</VuiTypography>
      <VuiBox sx={{ mt: 1 }}>
        <VuiInput placeholder="Ask the assistant (e.g. 'reconcile bank payment 2026-09-18 1200')" value={task} onChange={(e) => setTask(e.target.value)} />
      </VuiBox>
      <VuiBox sx={{ mt: 1, display: 'flex', gap: 1 }}>
        <VuiButton size="small" onClick={handleAsk} disabled={!tenantId || loading || !task}>Ask</VuiButton>
        <VuiButton size="small" onClick={() => { setTask(''); setResult(null); setError(null); setLicenseBlocked(false); }}>Clear</VuiButton>
      </VuiBox>

      {loading && <VuiTypography sx={{ mt: 1 }}>Loading…</VuiTypography>}

      {licenseBlocked && (
        <VuiBox sx={{ mt: 2, p: 2, borderRadius: 1, background: 'rgba(255,235,238,0.6)' }}>
          <VuiTypography color="error" sx={{ mb: 1 }}>AI-assisted confirmation statements are not enabled for this tenant.</VuiTypography>
          <VuiTypography variant="caption" sx={{ display: 'block', mb: 1 }}>Ask your organisation's super admin to enable the feature so the assistant can help with CS filings.</VuiTypography>
          {isSuperAdmin ? (
            <VuiButton size="small" variant="contained" onClick={() => history.push('/licenses')}>Open License Management</VuiButton>
          ) : (
            <VuiButton size="small" variant="outlined" onClick={() => { navigator.clipboard?.writeText(tenantId); alert('Tenant ID copied to clipboard'); }}>Copy tenant ID</VuiButton>
          )}
        </VuiBox>
      )}

      {error && !licenseBlocked && <VuiTypography color="error" sx={{ mt: 1 }}>{error}</VuiTypography>}

      {result && (
        <VuiBox sx={{ mt: 2 }}>
          <VuiTypography variant="button">Suggestions</VuiTypography>
          {Array.isArray(result.suggestions) && result.suggestions.map((s, i) => (
            <VuiBox key={i} sx={{ mt: 1, p: 1, borderRadius: 1, background: 'rgba(0,0,0,0.04)' }}>
              <VuiTypography variant="caption" sx={{ color: 'text.secondary' }}>{s.explanation}</VuiTypography>
              <pre style={{ whiteSpace: 'pre-wrap', marginTop: 6 }}>{JSON.stringify(s.payload, null, 2)}</pre>
              <VuiBox sx={{ mt: 1 }}>
                <VuiButton size="small" color="info" onClick={() => handleExecute(s)}>Validate (dry-run)</VuiButton>
              </VuiBox>
            </VuiBox>
          ))}

          {result.execResult && (
            <VuiBox sx={{ mt: 2 }}>
              <VuiTypography variant="caption">Execution result:</VuiTypography>
              <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(result.execResult, null, 2)}</pre>
            </VuiBox>
          )}
        </VuiBox>
      )}
    </VuiBox>
  );
}

